import { useTranslate } from '@refinedev/core';
import { Button, Tooltip, Typography } from 'antd';
import { useCallback, useMemo } from 'react';
import { learnVideoNameById } from '@cc/schema';
import { LEARN_ARTICLE_LABEL_IDS } from './learnArticles';
import { ASSESSMENT_TARGETS, TOOL_TARGETS } from './model';

type Target =
  | {
      type: string;
      ref?: string | null;
      version?: number | null;
      source?: string | null;
    }
  | null
  | undefined;

type Evidence = { focusType: string; focus: unknown } | null | undefined;

/** The evidence focus types that are not a Toolkit tool → the homework kind that reads them. */
const EVIDENCE_KIND: Record<string, string> = {
  assessment: 'assessment',
  learnVideo: 'video',
  // block 2a — a worksheet completes on its submission (or a consent on its signature)
  worksheetSubmission: 'worksheet',
  // block 6 — a practice's YouTube / Vimeo link watched to the end in the app (F-212: it read
  // "record: videoViewing", the matcher's internal focus type, on the hub and the session panel)
  videoViewing: 'video',
  // Q-be-1 B — a recorded observation of a behavior experiment completes its homework; it names the
  // OBSERVATION, not a kind (its label is `homework.evidence.observation`, read in `evidence` below)
};

/**
 * How a homework row's kind, target and evidence READ on the portal — one place, so the hub, the
 * session panel and the board say the same thing. Unknown values fall back to the raw id (a kind or
 * a tool a later block adds is shown, never hidden).
 */
export const useHomeworkLabels = () => {
  const t = useTranslate();

  const targetLabel = useCallback(
    (target: Target): string | null => {
      if (!target) return null;
      if ((TOOL_TARGETS as readonly string[]).includes(target.type))
        return t(`homework.target.tool.${target.type}`);
      if (target.type === 'assessment' && target.ref)
        return (ASSESSMENT_TARGETS as readonly string[]).includes(target.ref)
          ? t(`homework.target.assessment.${target.ref}`)
          : target.ref;
      if (target.type === 'article' && target.ref)
        return LEARN_ARTICLE_LABEL_IDS.includes(target.ref)
          ? t(`homework.articles.${target.ref}`)
          : target.ref;
      // E56 — a CopingCard Learn clip reads by its film's name; one of the seven retired English clips by the title it
      // always had (Q-learn-7 A) — an old assignment keeps its label instead of a bare YouTube id
      if (target.type === 'video' && target.source === 'learn' && target.ref) {
        return learnVideoNameById(target.ref) ?? target.ref;
      }
      if (target.type === 'url') return target.ref ?? t('homework.target.url');
      // block 2a — a worksheet's row carries the template's name as its title; the target says which
      // version the assignment pinned
      if (target.type === 'worksheet')
        return target.version
          ? t('homework.target.worksheetVersion', { version: target.version })
          : t('homework.kind.worksheet');
      return target.ref ? `${target.type} · ${target.ref}` : target.type;
    },
    [t],
  );

  const kindLabel = useCallback(
    (kind: string) => {
      const key = `homework.kind.${kind}`;
      const s = t(key);
      return s === key ? kind : s;
    },
    [t],
  );

  const kindTarget = useCallback(
    (kind: string, target: Target) => {
      const tl = targetLabel(target);
      return tl ? `${kindLabel(kind)} · ${tl}` : kindLabel(kind);
    },
    [kindLabel, targetLabel],
  );

  const evidence = useCallback(
    (ev: Evidence, opts?: { onOpenPhoto?: (fileRef: string) => void }) => {
      if (!ev) {
        return (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('homework.evidence.clientWord')}
          </Typography.Text>
        );
      }
      if (ev.focusType === 'paperPhoto') {
        // the photo opens in a viewer (the founder's Q-b1b-2 A) where the caller offers one
        if (opts?.onOpenPhoto && typeof ev.focus === 'string') {
          const fileRef = ev.focus;
          return (
            <Tooltip title={t('homework.evidence.paperPhotoHint')}>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', fontSize: 12 }}
                onClick={() => opts.onOpenPhoto?.(fileRef)}
                data-testid="homework-evidence-paper"
              >
                {t('homework.evidence.paperPhoto')}
              </Button>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={t('homework.evidence.paperPhotoHint')}>
            <Typography.Text
              style={{ fontSize: 12 }}
              data-testid="homework-evidence-paper"
            >
              {t('homework.evidence.paperPhoto')}
            </Typography.Text>
          </Tooltip>
        );
      }
      // the matcher's evidence (block 1d): a tool's record names the tool; a questionnaire or a Learn
      // video names its kind; a focus a later block adds is shown raw, never hidden
      const type = (TOOL_TARGETS as readonly string[]).includes(ev.focusType)
        ? t(`homework.target.tool.${ev.focusType}`)
        : ev.focusType === 'observation'
        ? t('homework.evidence.observation')
        : // E43 — a task's activity target met by the days the client's phone brought
        ev.focusType === 'activityDays'
        ? t('homework.evidence.activityDays', {
            count: Array.isArray(ev.focus) ? ev.focus.length : 1,
          })
        : ev.focusType in EVIDENCE_KIND
        ? kindLabel(EVIDENCE_KIND[ev.focusType])
        : ev.focusType;
      return (
        <Typography.Text
          style={{ fontSize: 12 }}
          data-testid="homework-evidence-record"
        >
          {t('homework.evidence.record', { type })}
        </Typography.Text>
      );
    },
    [kindLabel, t],
  );

  return useMemo(
    () => ({ targetLabel, kindLabel, kindTarget, evidence }),
    [targetLabel, kindLabel, kindTarget, evidence],
  );
};
